import { onboardingStage, type OnboardingStage } from "@/domain/payment-account/rules";
import type { PaymentAccount } from "@/domain/payment-account/types";
import { useActivities } from "@/features/activities/hooks/useActivities";
import { usePartnerOffers } from "@/features/offers/hooks/usePartnerOffers";
import { usePaymentAccount } from "@/features/settings/hooks/usePaymentAccount";
import { firstStepsCopy, paymentAccountStepBody } from "../first-steps-copy";

export interface FirstStep {
  title: string;
  body: string;
  to: string;
  cta: string;
  isDone: boolean;
}

function paymentAccountStage(account: PaymentAccount | null): OnboardingStage {
  return account === null ? "no-account" : onboardingStage(account);
}

function lastSuccessfulReadOf(account: PaymentAccount | null): Date | null {
  if (account === null || account.syncError === null) return null;
  return account.syncedAt;
}

/** The order a podnik has to be built in, and nothing else — every step links
 *  to the screen that owns the work rather than repeating it here.
 *
 *  The payment account leads because `is_tradeable` is what decides whether a
 *  customer sees anything the partner writes (lastgo-api ADR 0004); the other
 *  two are already done for every partner arriving from v1.
 *
 *  A signal still loading or failed makes the guidance silent: telling a
 *  partner their account is not set up because a request failed is worse than
 *  saying nothing, and the alternative flashes three open steps at a partner
 *  who finished them a year ago. */
export function useFirstSteps() {
  const { account, isLoading: isLoadingAccount, error: accountError } = usePaymentAccount();
  const { activities, isLoading: isLoadingActivities, error: activitiesError } = useActivities();
  const { offers, isLoading: isLoadingOffers, error: offersError } = usePartnerOffers();

  const isAnswered =
    !isLoadingAccount &&
    !isLoadingActivities &&
    !isLoadingOffers &&
    !accountError &&
    !activitiesError &&
    !offersError;

  const stage = paymentAccountStage(account);
  const canTrade = stage === "tradeable";
  const hasPublishedOffer = offers.length > 0;

  const steps: FirstStep[] = [
    {
      title: firstStepsCopy.paymentAccount.title,
      body: paymentAccountStepBody(stage, lastSuccessfulReadOf(account)),
      to: "/settings",
      cta: firstStepsCopy.paymentAccount.cta,
      isDone: canTrade,
    },
    {
      title: firstStepsCopy.activity.title,
      body: firstStepsCopy.activity.body,
      to: "/activities",
      cta: firstStepsCopy.activity.cta,
      isDone: activities.length > 0,
    },
    {
      title: firstStepsCopy.offer.title,
      body: firstStepsCopy.offer.body,
      to: "/offers",
      cta: firstStepsCopy.offer.cta,
      isDone: hasPublishedOffer,
    },
  ];

  return { steps, isGuidanceNeeded: isAnswered && !(canTrade && hasPublishedOffer) };
}
